import { definitions } from '@luxuryescapes/contract-trip'
import { Moment } from 'moment'

import {
  AcceptInviteResponse,
  AddBasicActivityToTripRequest,
  AddBasicActivityToTripResponse,
  ChatListResponse,
  ClaimChatsRequest,
  ClaimChatsResponse,
  CreateMessageRequest,
  CreateTripRequest,
  CreateTripResponse,
  DeleteChatResponse,
  DeleteInviteResponse,
  DeleteDayResponse,
  DeleteStopResponse,
  DeleteItemResponse,
  DeleteTripResponse,
  SubmitMessageFeedbackRequest,
  SubmitMessageFeedbackResponse,
  EditCollaboratorRequest,
  EditCollaboratorResponse,
  EditInviteRequest,
  EditInviteResponse,
  EnrichmentStatusResponse,
  GenerateMultiDestinationTripRequest,
  GenerateMustDoActivitiesRequest,
  GenerateMustDoActivitiesResponse,
  GenerateSuggestedLocationsRequest,
  GenerateSuggestedLocationsResponse,
  GenerateTripAiResponse,
  GenerateTripThemesRequest,
  GenerateTripThemesResponse,
  GenerateTripThemesV2Request,
  GenerateTripThemesV2Response,
  GetLowStockTripItemsResponse,
  GetNextPromptSuggestionsResponse,
  GetPlaceDetailResponse,
  GetPlaceDetailResult,
  GetPlaceHighlightsResult,
  GetPlaceInfoResult,
  GetTripCollaboratorsResponse,
  GetTripMetadataResponse,
  GetTripResponse,
  GetTripsResponse,
  GetPlaceAreasResult,
  GetPlaceBestTimeToVisitResult,
  GetPlaceInsiderTipsResult,
  GetPlaceOverviewResult,
  GetPlaceWhatToLoveResult,
  RecordTripViewResponse,
  RefreshTripResponse,
  RegenerateDayAiRequest,
  RegenerateDayAiResponse,
  RegenerateItemAiRequest,
  RegenerateItemAiResponse,
  RemoveCollaboratorResponse,
  SendInviteRequest,
  SendInviteResponse,
  TripPlannerChat,
  TripPlannerChatDetails,
  GenerateChatTitleResponse,
  UpdateChatRequest,
  UpdateChatResponse,
  UndoRegenerateDayAiRequest,
  UndoRegenerateDayAiResponse,
  UpdateTravelPreferencesRequest,
  UpdateTravelPreferencesResponse,
  UpdateTripRequest,
  UpdateTripResponse,
  UploadTripImageRequest,
  UploadTripImageResponse,
  GetItineraryResponse,
  ConvertChatToTripRequest,
  ConvertChatToTripResponse,
  ConvertChatToTripResult,
  LocationDrawImagesRequest,
  LocationDrawImagesResponse,
  LocationDrawInformationRequest,
  PlaceDrawImagesRequest,
  PlaceDrawImagesResponse,
} from './types'
import { baseUri, getOptions, extractApiErrorMessage } from './utils'

import request from 'api/requestUtils'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { SYDNEY_PLACE_INFO } from 'tripPlanner/config'
import { MustDoActivityItem } from 'tripPlanner/hooks/api/mustDoActivities'
import { ThemeItem } from 'tripPlanner/hooks/api/tripGenerationData'
import { TravellerRoom } from 'tripPlanner/types/common'

export type { ChatMessageAttachment } from './types'
export { fileToChatMessageAttachment } from './chatUtils'

export interface UploadFileResponse {
  id: string
  filename: string
}

export type PromptPresetResponse = {
  name: string
  promptId: string
  notes?: string | null
}

export function getTrip(
  tripId: string,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}`
  return request
    .get<GetTripResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getTripMetadata(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripMetadata']> {
  const uri = `${baseUri}/${tripId}/metadata`
  return request
    .get<GetTripMetadataResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function refreshTripOrders(
  tripId: string,
): Promise<definitions['refreshedTrip']> {
  const uri = `${baseUri}/${tripId}/refresh`
  return request
    .post<RefreshTripResponse, undefined>(uri, undefined, getOptions())
    .then((response) => response.result)
}

export function recordTripView(tripId: string): Promise<null> {
  const uri = `${baseUri}/${tripId}/record-view`
  return request
    .post<RecordTripViewResponse, undefined>(uri, undefined, getOptions())
    .then((response) => response.result)
}

export function getTrips(
  accessToken?: string,
): Promise<Array<definitions['basicTrip']>> {
  return request
    .get<GetTripsResponse>(baseUri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getTripCollaborators(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripCollaborators']> {
  const uri = `${baseUri}/${tripId}/collaborators`
  return request
    .get<GetTripCollaboratorsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function sendTripInvite(
  {
    tripId,
    email,
    role,
  }: {
    tripId: string
    email: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitation`
  return request
    .post<SendInviteResponse, SendInviteRequest>(
      uri,
      { email, role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function acceptTripInvite(
  {
    tripId,
    inviteId,
  }: {
    tripId: string
    inviteId: string
  },
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}/invitations/${inviteId}/accept`
  return request
    .post<AcceptInviteResponse, undefined>(
      uri,
      undefined,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function deleteTripInvite(
  {
    tripId,
    invitationId,
  }: {
    tripId: string
    invitationId: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitations/${invitationId}`
  return request
    .delete<DeleteInviteResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function removeCollaborator(
  {
    tripId,
    userId,
  }: {
    tripId: string
    userId: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/collaborators/${userId}`
  return request
    .delete<RemoveCollaboratorResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function editCollaborator(
  {
    tripId,
    userId,
    role,
  }: {
    tripId: string
    userId: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/collaborators/${userId}`
  return request
    .patch<EditCollaboratorResponse, EditCollaboratorRequest>(
      uri,
      { role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function editTripInvite(
  {
    tripId,
    invitationId,
    role,
  }: {
    tripId: string
    invitationId: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitations/${invitationId}`
  return request
    .patch<EditInviteResponse, EditInviteRequest>(
      uri,
      { role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function deleteTrip(id: string, accessToken?: string): Promise<null> {
  const uri = `${baseUri}/${id}`
  return request
    .delete<DeleteTripResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

interface CreateArgs {
  name: string
  startDate?: Moment
  endDate?: Moment
  imageId?: string
}

interface BaseTripActivity {
  name: string
  image: Array<string>
  city: string
  price: number
  currency: string
  availability: Array<string>
  category: Array<string>
  place: {
    type: string
    name: string
    address: string
    region: string
    point: {
      lat: number
      lng: number
    }
    photoUrl: string
  }
}

export interface LEExperienceTripActivity extends BaseTripActivity {
  offerId: string
}

export interface NonLEExperienceTripActivity extends BaseTripActivity {
  placeId: string
}

export function generateMultiDestinationTrip(
  params: GenerateMultiDestinationTripRequest,
  accessToken?: string,
): Promise<GenerateTripAiResponse['result']> {
  const uri = `${baseUri}/v2/generate-trip`
  return request
    .post<GenerateTripAiResponse, GenerateMultiDestinationTripRequest>(
      uri,
      params,
      {
        ...getOptions(accessToken),
      },
    )
    .then((response) => response.result)
}

export function generateThemes(
  params: GenerateTripThemesRequest,
): Promise<Array<ThemeItem>> {
  const uri = `${baseUri}/generate-themes`

  return request
    .post<GenerateTripThemesResponse, GenerateTripThemesRequest>(
      uri,
      params,
      {
        ...getOptions(),
      },
    )
    .then((response) => response.result.themes)
}

export function createChat(
  accessToken?: string,
): Promise<TripPlannerChat> {
  const uri = `${baseUri}/v2/chats`
  return request
    .post<App.ApiResponse<TripPlannerChat>, undefined>(uri, undefined, { ...getOptions(accessToken) })
    .then((response) => response.result)
}

export function stopMessageStream(
  params: { chatId: number },
  accessToken?: string,
): Promise<{ acknowledged: boolean }> {
  const uri = `${baseUri}/v2/messages/stop`
  return request
    .post<App.ApiResponse<{ acknowledged: boolean }>, { chatId: number }>(uri, params, { ...getOptions(accessToken) })
    .then(response => response.result)
}

export function submitMessageFeedback(
  chatId: number,
  messageId: string,
  reaction: 'UP' | 'DOWN',
  accessToken?: string,
): Promise<SubmitMessageFeedbackResponse> {
  const uri = `${baseUri}/v2/chats/${chatId}/messages/${messageId}/feedback`
  return request
    .post<App.ApiResponse<SubmitMessageFeedbackResponse>, SubmitMessageFeedbackRequest>(
      uri,
      { reaction },
      { ...getOptions(accessToken) },
    )
    .then(response => response.result)
}

export async function createMessageStream(
  params: CreateMessageRequest,
  accessToken?: string,
  onChunk?: (chunk: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const path = `${baseUri}/v2/messages`

  const isSSR = typeof window === 'undefined'
  const apiHost = isSSR ? (config.SERVER_SIDE_API_HOST ?? config.API_HOST) : config.API_HOST
  const endpoint = apiHost.startsWith('http') ? apiHost : `${config.SCHEME}://${apiHost}`
  const uri = `${endpoint}${path}`

  const options = getOptions(accessToken)

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  }

  const res = await fetch(uri, {
    method: 'POST',
    body: JSON.stringify(params),
    headers,
    credentials: options.credentials ?? 'include',
    signal,
  })

  if (!res.ok) {
    const errorText = await res.text().catch(() => '')
    throw new Error(extractApiErrorMessage(errorText, res.status))
  }

  if (!res.body) {
    throw new Error('Response body is null')
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')

  try {
    while (true) {
      const { value, done } = await reader.read()

      if (done) break
      if (!value) continue

      const chunk = decoder.decode(value, { stream: true })
      if (chunk && onChunk) {
        onChunk(chunk)
      }
    }

    const finalChunk = decoder.decode()
    if (finalChunk && onChunk) {
      onChunk(finalChunk)
    }
  } finally {
    reader.releaseLock?.()
  }
}

export function generateThemesV2(
  params: GenerateTripThemesV2Request,
): Promise<Array<ThemeItem>> {
  const uri = `${baseUri}/v2/generate-themes`
  return request
    .post<GenerateTripThemesV2Response, GenerateTripThemesV2Request>(uri, params, { ...getOptions() })
    .then((response) => response.result.themes)
}

export function generateMustDoActivities(
  params: GenerateMustDoActivitiesRequest,
): Promise<Array<MustDoActivityItem>> {
  const uri = `${baseUri}/generate-must-do-activities`
  return request
    .post<GenerateMustDoActivitiesResponse, GenerateMustDoActivitiesRequest>(
      uri,
      params,
      { ...getOptions() },
    )
    .then((response) => response.result.mustDoActivities.map((activity: MustDoActivityItem) => ({
      activity: activity.activity,
      description: activity.description,
      imageId: activity.imageId,
      imageUrl: activity.imageUrl,
    })))
}

export function regenerateIndividualItem(
  params: RegenerateItemAiRequest,
  tripId: string,
  accessToken?: string,
): Promise<RegenerateItemAiResponse['result']['activities']> {
  const uri = `${baseUri}/${tripId}/generate-alternative-item`
  return request
    .post<RegenerateItemAiResponse, RegenerateItemAiRequest>(uri, params, { ...getOptions(accessToken) })
    .then((response) => response.result.activities)
}

export function addBasicActivityToTrip(
  params: AddBasicActivityToTripRequest,
  tripId: string,
  accessToken?: string,
): Promise<AddBasicActivityToTripResponse['result']> {
  const uri = `${baseUri}/${tripId}/basic-activity`
  return request
    .post<AddBasicActivityToTripResponse, AddBasicActivityToTripRequest>(uri, params, { ...getOptions(accessToken) })
    .then((response) => response.result)
}

export function generateSuggestedLocations(
  params: GenerateSuggestedLocationsRequest,
  accessToken?: string,
): Promise<GenerateSuggestedLocationsResponse['result']> {
  const uri = `${baseUri}/generate-suggested-locations`
  return request
    .post<GenerateSuggestedLocationsResponse, GenerateSuggestedLocationsRequest>(uri, params, { ...getOptions(accessToken) })
    .then((response) => response.result)
}

type ActivityLocationImage = {
  id: string
  name: string
  placeId: string
  imageIds: Array<string>
}

export function getLocationImage(
  placeId: string,
): Promise<Array<ActivityLocationImage>> {
  const uri = `${baseUri}/activity-location-images/${placeId}`
  return request
    .get<{ result: Array<ActivityLocationImage> }>(uri, getOptions())
    .then((response) => response.result.map(item => ({
      id: item.id,
      name: item.name,
      placeId: item.placeId,
      imageIds: item.imageIds,
    })))
}

export interface RegenerateDayAiParams {
  tripId: string
  date: string
  userInput?: string
  alternativeGroup?: string
  itemId?: string
}

export function regenerateDayAi(
  params: RegenerateDayAiParams,
  accessToken?: string,
): Promise<RegenerateDayAiResponse['result']> {
  const uri = `${baseUri}/regenerate-day`
  return request
    .post<RegenerateDayAiResponse, RegenerateDayAiRequest>(
      uri,
      params,
      { ...getOptions(accessToken), timeout: 120000 },
    )
    .then((response) => response.result)
}

export function undoRegenerateDayAi(
  params: UndoRegenerateDayAiRequest,
  accessToken?: string,
): Promise<UndoRegenerateDayAiResponse['result']> {
  const uri = `${baseUri}/undo-regenerate-day`
  return request
    .post<UndoRegenerateDayAiResponse, UndoRegenerateDayAiRequest>(
      uri,
      params,
      { ...getOptions(accessToken), timeout: 120000 },
    )
    .then((response) => response.result)
}

export function updateTravelPreferences(
  params: UpdateTravelPreferencesRequest,
  accessToken?: string,
): Promise<{ message: string } | null> {
  const uri = `${baseUri}/travel-preferences`
  return request
    .post<UpdateTravelPreferencesResponse, UpdateTravelPreferencesRequest>(
      uri,
      params,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}
export function createTrip(
  { name, startDate, endDate, imageId }: CreateArgs,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  return request
    .post<CreateTripResponse, CreateTripRequest>(
      baseUri,
      {
        name,
        originPlaceId: SYDNEY_PLACE_INFO.id, // TODO: remove this param from the API
        startDate: startDate?.format(ISO_DATE_FORMAT),
        endDate: endDate?.format(ISO_DATE_FORMAT),
        imageId,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

interface UpdateArgs {
  tripId: string
  name?: string
  startDate?: Moment | undefined | null
  endDate?: Moment | undefined | null
  imageId?: string
  originPlaceId?: string | null
  destinationPlaceIds?: Array<string>
  travellerRooms?: Array<TravellerRoom>
}

export function updateTrip(
  args: UpdateArgs,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const {
    tripId,
    name,
    imageId,
    originPlaceId,
    destinationPlaceIds,
    travellerRooms,
  } = args

  const uri = `${baseUri}/${tripId}`

  let startDate: string | null | undefined = undefined
  if (Object.prototype.hasOwnProperty.call(args, 'startDate')) {
    startDate = args.startDate ? args.startDate.format(ISO_DATE_FORMAT) : null
  }

  let endDate: string | null | undefined = undefined
  if (Object.prototype.hasOwnProperty.call(args, 'endDate')) {
    endDate = args.endDate ? args.endDate.format(ISO_DATE_FORMAT) : null
  }

  return request
    .patch<UpdateTripResponse, UpdateTripRequest>(
      uri,
      {
        name,
        startDate,
        endDate,
        imageId,
        originPlaceId,
        destinationPlaceIds,
        travellerRooms,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function fetchUser(
  {
    email,
  }: {
    email: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `/api/users/${email}`
  return request
    .get<App.ApiResponse<null>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function uploadTripImage(
  {
    tripId,
    imageData,
  }: {
    tripId: string
    imageData: string
  },
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}/trip-image`
  return request
    .post<UploadTripImageResponse, UploadTripImageRequest>(
      uri,
      {
        imageData,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export async function getLowStockItemsForTrip(
  {
    tripId,
  }: {
    tripId: string
  },
  accessToken?: string,
): Promise<Array<definitions['lowStockItem']>> {
  const uri = `${baseUri}/${tripId}/low-stock-items`

  return request
    .get<GetLowStockTripItemsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function uploadFile(
  file: File,
  accessToken?: string,
): Promise<UploadFileResponse> {
  const uri = '/api/trips/v2/files'
  const formData = new FormData()
  formData.append('file', file)

  return request
    .upload<App.ApiResponse<UploadFileResponse>>({
      path: uri,
      data: formData,
      options: getOptions(accessToken),
    })
    .then((response) => response.result)
}

export function getChat(
  chatId: number,
  accessToken?: string,
): Promise<TripPlannerChatDetails> {
  const uri = `${baseUri}/v2/chats/${chatId}`
  return request
    .get<App.ApiResponse<TripPlannerChatDetails>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function listChats(accessToken?: string): Promise<ChatListResponse> {
  const uri = `${baseUri}/v2/chats`
  return request
    .get<App.ApiResponse<ChatListResponse>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function listPromptPresets(accessToken?: string): Promise<Array<PromptPresetResponse>> {
  const uri = `${baseUri}/v2/prompts`
  return request
    .get<App.ApiResponse<Array<PromptPresetResponse>>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function createPromptPreset(
  body: { name: string; promptId: string; notes?: string },
  accessToken?: string,
): Promise<PromptPresetResponse> {
  const uri = `${baseUri}/v2/prompts`
  return request
    .post<App.ApiResponse<PromptPresetResponse>, typeof body>(uri, body, getOptions(accessToken))
    .then((response) => response.result)
}

export function updatePromptPreset(
  promptId: string,
  body: { name: string; promptId: string; notes?: string },
  accessToken?: string,
): Promise<PromptPresetResponse> {
  const uri = `${baseUri}/v2/prompts/${promptId}`
  return request
    .put<App.ApiResponse<PromptPresetResponse>, typeof body>(uri, body, getOptions(accessToken))
    .then((response) => response.result)
}

export function deletePromptPreset(promptId: string, accessToken?: string): Promise<null> {
  const uri = `${baseUri}/v2/prompts/${promptId}`
  return request
    .delete<App.ApiResponse<null>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function deleteChat(
  chatId: number,
  accessToken?: string,
): Promise<DeleteChatResponse> {
  const uri = `${baseUri}/v2/chats/${chatId}`
  return request
    .delete<App.ApiResponse<DeleteChatResponse>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function deleteStop(
  stopId: number,
  accessToken?: string,
): Promise<DeleteStopResponse> {
  const uri = `${baseUri}/v2/stops/${stopId}`
  return request
    .delete<App.ApiResponse<DeleteStopResponse>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function deleteDay(
  dayId: number,
  accessToken?: string,
): Promise<DeleteDayResponse> {
  const uri = `${baseUri}/v2/days/${dayId}`
  return request
    .delete<App.ApiResponse<DeleteDayResponse>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function deleteItem(
  itemId: number,
  accessToken?: string,
): Promise<DeleteItemResponse> {
  const uri = `${baseUri}/v2/items/${itemId}`
  return request
    .delete<App.ApiResponse<DeleteItemResponse>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function claimChats(
  params: ClaimChatsRequest,
  accessToken?: string,
): Promise<ClaimChatsResponse> {
  const uri = `${baseUri}/v2/chats/claim`
  return request
    .post<App.ApiResponse<ClaimChatsResponse>, ClaimChatsRequest>(uri, params, getOptions(accessToken))
    .then((response) => response.result)
}

export function generateChatTitle(
  params: {
    chatId: number
    message: string
  },
  accessToken?: string,
): Promise<GenerateChatTitleResponse> {
  const uri = `${baseUri}/v2/chats/${params.chatId}/title`
  return request
    .post<App.ApiResponse<GenerateChatTitleResponse>, { message: string }>(
      uri,
      { message: params.message },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function updateChat(
  params: { chatId: number } & UpdateChatRequest,
  accessToken?: string,
): Promise<UpdateChatResponse> {
  const { chatId, ...payload } = params
  const uri = `${baseUri}/v2/chats/${chatId}`
  return request
    .put<App.ApiResponse<UpdateChatResponse>, UpdateChatRequest>(uri, payload, getOptions(accessToken))
    .then((response) => response.result)
}

export function getNextPromptSuggestions(
  chatId: number,
  accessToken?: string,
): Promise<GetNextPromptSuggestionsResponse['result']> {
  const uri = `${baseUri}/v2/chats/${chatId}/suggestions`
  return request
    .get<GetNextPromptSuggestionsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getItinerary(
  chatId: number,
  accessToken?: string,
): Promise<GetItineraryResponse['result']> {
  const uri = `${baseUri}/v2/chats/${chatId}/itinerary`
  return request
    .get<GetItineraryResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getEnrichmentStatus(
  chatId: number,
  params: { stopIds?: Array<number>; itemIds?: Array<number> },
  accessToken?: string,
): Promise<EnrichmentStatusResponse> {
  const queryParams = new URLSearchParams()
  if (params.stopIds?.length) {
    queryParams.set('stopIds', params.stopIds.join(','))
  }
  if (params.itemIds?.length) {
    queryParams.set('itemIds', params.itemIds.join(','))
  }

  const uri = `${baseUri}/v2/chats/${chatId}/enrichment-status?${queryParams.toString()}`
  return request
    .get<App.ApiResponse<EnrichmentStatusResponse>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceDetail(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceDetailResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}`
  return request
    .get<GetPlaceDetailResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export interface DeletePlaceCacheResponse {
  status: number
  result: {
    deleted: number
  }
}

export function deletePlaceCache(
  googlePlaceId?: string,
  accessToken?: string,
): Promise<{ deleted: number }> {
  const uri = googlePlaceId
    ? `${baseUri}/v2/admin/place-cache/${googlePlaceId}`
    : `${baseUri}/v2/admin/place-cache`
  return request
    .delete<DeletePlaceCacheResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

// Place Detail Section Endpoints - for progressive loading

export function getPlaceOverview(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceOverviewResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}/overview`
  return request
    .get<App.ApiResponse<GetPlaceOverviewResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceBestTimeToVisit(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceBestTimeToVisitResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}/best-time-to-visit`
  return request
    .get<App.ApiResponse<GetPlaceBestTimeToVisitResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceWhatToLove(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceWhatToLoveResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}/what-to-love`
  return request
    .get<App.ApiResponse<GetPlaceWhatToLoveResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceAreas(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceAreasResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}/areas`
  return request
    .get<App.ApiResponse<GetPlaceAreasResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceInsiderTips(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceInsiderTipsResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}/insider-tips`
  return request
    .get<App.ApiResponse<GetPlaceInsiderTipsResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceInfo(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceInfoResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}/info`
  return request
    .get<App.ApiResponse<GetPlaceInfoResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getPlaceHighlights(
  googlePlaceId: string,
  accessToken?: string,
): Promise<GetPlaceHighlightsResult> {
  const uri = `${baseUri}/v2/places/${googlePlaceId}/highlights`
  return request
    .get<App.ApiResponse<GetPlaceHighlightsResult>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function convertChatToTrip(
  chatId: number,
  params: ConvertChatToTripRequest,
  accessToken?: string,
): Promise<ConvertChatToTripResult> {
  const uri = `${baseUri}/v2/chats/${chatId}/convert-to-trip`
  return request
    .post<ConvertChatToTripResponse, ConvertChatToTripRequest>(
      uri,
      params,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function getLocationDrawImages(
  params: LocationDrawImagesRequest,
  accessToken?: string,
): Promise<LocationDrawImagesResponse['result']> {
  const uri = `${baseUri}/v2/location-draw-images`
  return request
    .post<LocationDrawImagesResponse, LocationDrawImagesRequest>(
      uri,
      params,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function getPlaceDrawImages(
  params: PlaceDrawImagesRequest,
  accessToken?: string,
): Promise<PlaceDrawImagesResponse['result']> {
  const uri = `${baseUri}/v2/place-draw-images`
  return request
    .post<PlaceDrawImagesResponse, PlaceDrawImagesRequest>(
      uri,
      params,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export async function createLocationDrawInformationStream(
  params: LocationDrawInformationRequest,
  accessToken?: string,
  onChunk?: (chunk: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const path = `${baseUri}/v2/location-draw-information`

  const isSSR = typeof window === 'undefined'
  const apiHost = isSSR ? (config.SERVER_SIDE_API_HOST ?? config.API_HOST) : config.API_HOST
  const endpoint = apiHost.startsWith('http') ? apiHost : `${config.SCHEME}://${apiHost}`
  const uri = `${endpoint}${path}`

  const options = getOptions(accessToken)

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  }

  const res = await fetch(uri, {
    method: 'POST',
    body: JSON.stringify(params),
    headers,
    credentials: options.credentials ?? 'include',
    signal,
  })

  if (!res.ok) {
    const errorText = await res.text().catch(() => '')
    throw new Error(extractApiErrorMessage(errorText, res.status))
  }

  if (!res.body) {
    throw new Error('Response body is null')
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')

  try {
    while (true) {
      const { value, done } = await reader.read()

      if (done) break
      if (!value) continue

      const chunk = decoder.decode(value, { stream: true })
      if (chunk && onChunk) {
        onChunk(chunk)
      }
    }

    const finalChunk = decoder.decode()
    if (finalChunk && onChunk) {
      onChunk(finalChunk)
    }
  } finally {
    reader.releaseLock?.()
  }
}
